import { AvantioAccommodation, Integration, LocalEditableIntegration } from "../../_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CapsuleButton, CustomNumberInput, Selector } from "../../_components";
import { useDispatch, useSelector } from "react-redux";
import { integrationsActions } from "../../_actions/integrations.actions";
import { uiAlertActions } from "../../_actions/uiAlert.actions";
import { Selectors } from "../../_reducers/app.reducer";
import { INVITATION_TYPES, MIN_TIME, TIME_FORMAT } from "../../_constants";
import { useError } from "../../_hooks/useError";
import { Icons } from "../../_assets";
import { IntegrationStore } from "../../_stores/IntegrationStore";
import { AvantioStore } from "../../_stores/AvantioStore";
import { avantioActions } from "../../_actions/avantio.actions";
import TimePicker from "rc-time-picker";
import moment from "moment";
import { TimeHelper } from "../../_helpers";


type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onSetInitialStatus: (...args: any) => void | any;
    groups: { [key: number]: any[]; };
    onDelete: (index: number) => void;
    index: number;
}

export function AvantioIntegrationInput({ integration, onDelete, index, onSetInitialStatus, groups, onToggleEditable }: IntegrationInputProps) {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const buildOptions = useCallback((availableGroups: any[] = []) => {
        return availableGroups.map((group) => ({ value: group.id, label: group.name, isDisabled: group.isDisabled })) ?? [];
    }, []);

    const integrations = useSelector(IntegrationStore.selectorGetItems);
    const selectedIntegrationUid: string = useSelector(IntegrationStore.selectorGetSelectedId);

    const buildGroupGroupedOptions = useCallback((groups: { [key: number]: any[] }) => {
        return [
            {
                label: t("global_typeof_location"),
                options: buildOptions(groups[INVITATION_TYPES.LOCATION]),
            },
            {
                label: t("global_typeof_room"),
                options: buildOptions(groups[INVITATION_TYPES.ROOM]),
            },
        ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allGroups = useMemo(() => [...(groups[INVITATION_TYPES.LOCATION] ?? []), ...(groups[INVITATION_TYPES.ROOM]) ?? []], [groups]);
    const baseIntegration = useMemo(() => integration, [integration]);
    const [apikeyValue, setApikeyValue] = useState(integration.apikey ?? '');
    const [availableGroups, setAvailableGroups] = useState(buildGroupGroupedOptions(groups));
    const integrationGroups = integration.groups || [];
    const [selectedGroupsLocal, setSelectedGroupsLocal] = useState(integrationGroups.map(({id}) => ({value: id})) ?? []);
    const [createPublicUrl, setCreatePublicUrl] = useState(integration.createPublicUrl ?? true);
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL ?? true);
    const [daysForInvitationCreate, setDaysForInvitationCreate] = useState(integration.daysForInvitationCreate ?? 1);
    const { errorMessage, setError, hasError, disableError } = useError();
    const locationUid = useSelector(Selectors.getSelectedLocationUid) ?? '';

    const buildPropertyOptions = useCallback((availableProperties: AvantioAccommodation[] = []) => {
        return availableProperties.map((property) => ({ value: property.id, label: property.name, isDisabled: false })) ?? [];
    }, []);

    const accommodations = useSelector(AvantioStore.selectorGetItems);
    const [selectedPropertyLocal, setSelectedPropertyLocal] = useState({
        value: integration.host_id,
        label: integration.host_name,
        isDisabled: false,
    });
    const [availableProperties, setAvailableProperties] = useState(buildPropertyOptions(accommodations));

    const onChangeProperty = useCallback(selectedProperty => {
        setSelectedPropertyLocal(selectedProperty);
        dispatch(AvantioStore.actionSelectItem(selectedProperty.value));
    }, [dispatch]);

    const onSeeReservations = useCallback(async (integrationUid) => {
        await dispatch(integrationsActions.listReservations(locationUid, integrationUid));
        dispatch(IntegrationStore.actionSelectItem(integrationUid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationUid]);

    const onChangeGroups = useCallback(selectedGroups => {
        disableError();
        setAvailableGroups(buildGroupGroupedOptions(groups));
        setSelectedGroupsLocal(selectedGroups);
    }, [buildGroupGroupedOptions, groups]);

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);
    const timeNoSecondsFormat = TimeHelper.getTimeFormats().TIME_NO_SECONDS;
    const getHourAndMinutesFormat = useCallback((time: string) => moment(time, timeNoSecondsFormat), []);

    useEffect(() => {
        setApikeyValue(integration.apikey ?? '');
        setCreatePublicUrl(integration.createPublicUrl ?? true);
        setSendInvitationURL(integration.sendInvitationURL ?? true);
        setDaysForInvitationCreate(integration.daysForInvitationCreate ?? 1);
        setCheckIn(integration.check_in ?? '16:00');
        setCheckOut(integration.check_out ?? '12:00');
        setSelectedPropertyLocal({
            value: integration.host_id,
            label: integration.host_name,
            isDisabled: false,
        });

        const selected = buildOptions(allGroups.filter(({ id }) => integrationGroups.map(({id}) => id).includes(id)));
        onChangeGroups(selected);
    }, [allGroups, buildOptions, groups, integration, onChangeGroups]);

    useEffect(() => {
        if (!!integration.uid) {
            dispatch(avantioActions.listAccommodations(integration.uid));
        }
    }, [dispatch, integration.uid]);

    useEffect(() => {
        setAvailableProperties(buildPropertyOptions(accommodations));
    }, [accommodations, buildPropertyOptions]);

    const canValidate = useMemo(() => {
        if (hasError) return false;
        return !!apikeyValue && selectedGroupsLocal.length !== 0;
    }, [apikeyValue, hasError, selectedGroupsLocal]);

    const onValidate = useCallback(async () => {
        if (!apikeyValue) {
            setError(t('mod_integrations_apikey_required'));
            return;
        }

        const editedIntegration: Integration = {
            apikey: apikeyValue,
            company: integration.company,
            uid: integration.uid,
            groups: selectedGroupsLocal.map(({ value }) => ({ id: value })),
            createPublicUrl,
        }
        if (locationUid) {
            try {
                await dispatch(integrationsActions.createIntegration(locationUid, editedIntegration));
            } catch (err: any) {
                setError(err.message);
            }
        }
    }, [dispatch, setError, integration.company, integration.uid, apikeyValue, locationUid, selectedGroupsLocal, t, createPublicUrl]);

    const canSave = useMemo(() => {
        if (hasError) return false;

        const baseGroups = baseIntegration.groups || [];
        const groupsWithoutDifferences = baseGroups.every(({id}) =>
            selectedGroupsLocal.map(({ value }) => value).includes(id),
        ) && baseGroups.length === selectedGroupsLocal.length;

        return (
            !!apikeyValue &&
            !!selectedPropertyLocal?.value &&
            selectedGroupsLocal.length !== 0 &&
            (
                apikeyValue !== baseIntegration.apikey ||
                selectedPropertyLocal.value !== baseIntegration.host_id ||
                !groupsWithoutDifferences ||
                createPublicUrl !== integration.createPublicUrl ||
                sendInvitationURL !== integration.sendInvitationURL ||
                daysForInvitationCreate !== integration.daysForInvitationCreate ||
                checkIn !== integration.check_in ||
                checkOut !== integration.check_out
            )
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseIntegration.groups, baseIntegration.apikey, baseIntegration.host_id, createPublicUrl, integrations, integration.createPublicUrl, apikeyValue, selectedGroupsLocal, sendInvitationURL, selectedPropertyLocal, daysForInvitationCreate, checkIn, checkOut]);

    const onSave = useCallback(async () => {
        const editedIntegration: Integration = {
            apikey: apikeyValue,
            host_id: selectedPropertyLocal.value,
            host_name: selectedPropertyLocal.label,
            company: integration.company,
            createPublicUrl,
            groups: selectedGroupsLocal.map(({ value }) => ({ id: value })),
            uid: integration.uid,
            sendInvitationURL,
            daysForInvitationCreate,
            check_in: checkIn,
            check_out: checkOut,
        }
        if (locationUid) {
            try {
                await dispatch(integrationsActions.createIntegration(locationUid, editedIntegration));
                dispatch(uiAlertActions.success(t("mod_integrations_saved_successfully")));
            } catch (err: any) {
                setError(err.message);
            }
        }
    }, [createPublicUrl, dispatch, setError, t, integration.company, integration.uid, apikeyValue, locationUid, selectedGroupsLocal, sendInvitationURL, selectedPropertyLocal, daysForInvitationCreate, checkIn, checkOut]);

    const isLegacyIcal = useMemo(() => !!integration.uid && !integration.apikey, [integration.uid, integration.apikey]);

    if (isLegacyIcal) {
        return (
            <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
                <div className="col-span-full">
                    <input
                        className="cna col-span-full w-100 p-2 c-h-min c-input-bordered"
                        type="text"
                        readOnly
                        value={integration.link ?? ''}
                        placeholder={t("global_ical_link")}
                        onChange={() => undefined}
                    />
                </div>
                <div className="d-flex justify-content-end">
                    <CapsuleButton
                        onClick={() => onDelete(index)}
                        classNameExtra="mr-2"
                        // eslint-disable-next-line react/style-prop-object
                        style="DANGER"
                        size="SMALL"
                        icon={Icons.trash}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="c-grid c-grid--small w-100 c-bg-grey-250 p-4 rounded-lg">
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">API Key</label>
                <input
                    className={`${hasError ? 'input-error' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    value={apikeyValue}
                    placeholder={"API Key"}
                    onChange={(e) => {
                        setApikeyValue(e.target.value?.trim());
                        disableError();
                    }}
                />
            </div>

            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">{t("mod_invitations_select_group")}</label>
                <Selector
                    hideSelectedOptions={false}
                    placeholder={t("mod_invitations_select_group")}
                    isClearable
                    value={selectedGroupsLocal[0] ?? null}
                    onChange={(selectedGroup: any) =>
                        onChangeGroups(selectedGroup ? [selectedGroup] : [])
                    }
                    options={availableGroups}
                />
            </div>

            {!integration.uid && (
                <div className="col-span-full d-flex justify-content-end">
                    <CapsuleButton
                        size="SMALL"
                        isEnabled={canValidate}
                        onClick={onValidate}
                        text={t("global_validate")}
                    />
                </div>
            )}

            {integration.uid && (
                <>
                    <hr className="col-span-full my-2 w-100" />

                    <div className="col-span-full">
                        <label className="font-weight-bold mb-1 d-block">{t("mod_integrations_select_property")}</label>
                        <Selector
                            hideSelectedOptions={false}
                            placeholder={t("mod_integrations_select_property")}
                            value={selectedPropertyLocal}
                            onChange={(selectedProperty: any) =>
                                onChangeProperty(selectedProperty)
                            }
                            isDisabled={availableProperties.length === 0}
                            options={availableProperties}
                        />
                    </div>

                    <div className="col-span-full d-flex" style={{ gap: "16px" }}>
                        <div className="flex-grow-1">
                            <label className="font-weight-bold mb-1 d-block">Check In</label>
                            <TimePicker
                                showSecond={false}
                                minuteStep={5}
                                value={getHourAndMinutesFormat(checkIn)}
                                format={TIME_FORMAT}
                                onChange={(value) =>
                                    setCheckIn(value ? value?.format(TIME_FORMAT) : MIN_TIME)
                                }
                            />
                        </div>
                        <div className="flex-grow-1">
                            <label className="font-weight-bold mb-1 d-block">Check Out</label>
                            <TimePicker
                                showSecond={false}
                                minuteStep={5}
                                value={getHourAndMinutesFormat(checkOut)}
                                format={TIME_FORMAT}
                                onChange={(value) =>
                                    setCheckOut(value ? value?.format(TIME_FORMAT) : MIN_TIME)
                                }
                            />
                        </div>
                    </div>

                    <div className="col-span-full d-flex align-items-center justify-content-between">
                        <label className="font-weight-bold mb-0">{t("mod_integrations_days_for_invitation_create")}</label>
                        <CustomNumberInput
                            value={daysForInvitationCreate}
                            min={1}
                            max={30}
                            onChange={setDaysForInvitationCreate}
                        />
                    </div>
                </>
            )}

            <div className="col-span-full d-flex justify-content-between align-items-center mt-2">
                <div>
                    {integration.host_id && (
                        <CapsuleButton
                            isEnabled={integration.uid !== selectedIntegrationUid}
                            onClick={() => onSeeReservations(integration.uid)}
                            // eslint-disable-next-line react/style-prop-object
                            size="SMALL"
                            text={t('mod_integrations_see_reservations')}
                        />
                    )}
                </div>
                <div className="d-flex">
                    <CapsuleButton
                        onClick={() => onDelete(index)}
                        classNameExtra="mr-2"
                        // eslint-disable-next-line react/style-prop-object
                        style="DANGER"
                        size="SMALL"
                        icon={Icons.trash}
                    />
                    {integration.uid && (
                        <CapsuleButton
                            size="SMALL"
                            isEnabled={canSave}
                            onClick={onSave}
                            text={t("global_save")}
                        />
                    )}
                </div>
            </div>
            {errorMessage && (
                <div className="col-span-full text-danger">{errorMessage}</div>
            )}
        </div>
    );
}
